import { useMemo } from 'react';
import { useInfiniteQuery } from 'react-query';

import _ from 'lodash';

import { baseApiUrl, fetcher, ServerErrorResponse, createParamString } from 'data_sources/constants';
import datetimeHelper from 'helpers/datetimeHelper';
import { generateRiskPoolMetrics } from './riskPoolMetricsGenerator';
import { useRiskPoolDemoData } from './useRiskPoolDemoData';

import type {
  RiskPoolOrganization,
  RiskPoolOrganizationsApiResponse,
  RiskPoolOrganizationsResponse,
} from '../../_types/risk_pool';

interface RiskPoolOrganizationsParams {
  startDate?: string;
  endDate?: string;
  search?: string;
  trend?: string;
  compliance?: string;
  policyMods?: string;
  limit?: number;
  offset?: number;
  [key: string]: any;
}

/** Default date range for risk-pool organizations API: start of previous month through end of current month. */
const getDefaultRiskPoolDateRange = (): { startDate: string; endDate: string } => {
  const today = new Date();
  const startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };
};

// Apply filters to organizations data
const applyFilters = (data: RiskPoolOrganization[], params: RiskPoolOrganizationsParams): RiskPoolOrganization[] => {
  let filteredData = [...data];

  // Apply search filter if search param is not empty
  if (!_.isEmpty(params.search)) {
    filteredData = _.filter(filteredData, (org) => org.name.toLowerCase().includes(params.search!.toLowerCase()));
  }

  // Apply trend filter if trend param is not empty
  if (!_.isEmpty(params.trend)) {
    filteredData = _.filter(filteredData, (org) => org.trend === params.trend);
  }

  // Apply compliance filter if compliance param is not empty
  if (!_.isEmpty(params.compliance)) {
    filteredData = _.filter(filteredData, (org) => org.compliancePerformance === params.compliance);
  }

  // Apply policy modifications filter if policyMods param is not empty
  if (!_.isEmpty(params.policyMods)) {
    filteredData = _.filter(filteredData, (org) => {
      if (params.policyMods === 'YES') return org.modifiedPolicyCount > 0;
      if (params.policyMods === 'NO') return org.modifiedPolicyCount === 0;
      return true;
    });
  }

  return filteredData;
};

export const getRiskPoolOrganizations = async (
  orgId: string,
  params: RiskPoolOrganizationsParams,
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void
): Promise<RiskPoolOrganizationsResponse> => {
  const url = `${baseApiUrl()}/activity-center/v1/organizations/${orgId}/risk-pool/policy/organizations`;

  const defaultDates = getDefaultRiskPoolDateRange();
  const apiParams = {
    ...params,
    limit: params.limit ?? 25,
    startDate: params.startDate ?? defaultDates.startDate,
    endDate: params.endDate ?? defaultDates.endDate,
  };

  const paramString = createParamString(apiParams);

  try {
    const apiData: RiskPoolOrganizationsApiResponse = await fetcher({
      method: 'get',
      url: `${url}?${paramString}`,
      options: { signal },
    });
    // Return both organizations and metadata
    return {
      organizations: apiData.content,
      totalCount: apiData.count,
      offset: apiData.offset,
    };
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    }
    throw error;
  }
};

export default function useRiskPoolOrganizations(
  orgId: string,
  params: RiskPoolOrganizationsParams = {},
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const isDemoDataEnabled = useRiskPoolDemoData();
  const areDatesValid =
    params.startDate && params.endDate
      ? datetimeHelper.isValidDateString(params.startDate) && datetimeHelper.isValidDateString(params.endDate)
      : true;

  const paramsWithDefaults = useMemo(() => {
    const defaultDates = getDefaultRiskPoolDateRange();
    return {
      ...params,
      startDate: params.startDate ?? defaultDates.startDate,
      endDate: params.endDate ?? defaultDates.endDate,
    };
  }, [params]);

  return useInfiniteQuery<RiskPoolOrganizationsResponse, Error>(
    ['risk_pool_organizations', orgId, paramsWithDefaults],
    async ({ signal, pageParam }): Promise<RiskPoolOrganizationsResponse> => {
      if (isDemoDataEnabled) {
        // Generate mock organizations and apply filters
        let data = generateRiskPoolMetrics(orgId, paramsWithDefaults);
        data = applyFilters(data, paramsWithDefaults);
        return {
          organizations: data,
          totalCount: data.length,
          offset: 0,
        };
      }

      // Get data from API (always includes startDate/endDate via defaults in getRiskPoolOrganizations)
      const offset = pageParam?.offset ?? params.offset ?? 0;
      return await getRiskPoolOrganizations(orgId, { ...paramsWithDefaults, offset }, signal, onError);
    },
    {
      ...options,
      enabled: !!orgId && areDatesValid,
      keepPreviousData: true,
      refetchOnWindowFocus: true,
      refetchInterval: isDemoDataEnabled ? false : 1000 * 60 * 2, // 2 minutes, disabled for demo data
      getNextPageParam: (lastPage) => {
        // Check if there are more pages to fetch
        if (lastPage.offset && lastPage.offset < lastPage.totalCount && lastPage.organizations.length > 0) {
          return { offset: lastPage.offset };
        }
        // No more pages
        return undefined;
      },
    }
  );
}
